import React from 'react';
import PropTypes from 'prop-types';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    WATCHLIST_CONTEXT_FIELDS as CONTEXT_FIELDS,
    WATCHLIST_CONTEXT_PRIMARY_FACTOR_FIELDS as PRIMARY_FACTOR_FIELDS,
    WATCHLIST_CONTEXT_RELEASE_FIELDS as RELEASE_FIELDS,
    WATCHLIST_CONTEXT_SETTING_FIELDS as SETTING_FIELDS,
    WATCHLIST_CONTEXT_SOURCE_RULE_FIELDS as SOURCE_RULE_FIELDS,
} from '../../context/applicationContextPresentation';
import { watchlistTableFrameSx } from '../../styles/watchlistMsiStyles';

const display = (value) => (value === null || value === undefined || value === '' ? '—' : String(value));
const joined = (...values) => values.map(display).filter((value) => value !== '—').join(' · ') || '—';

function contextRows(saved, available, changedFields) {
    const changed = new Set(changedFields);
    CONTEXT_FIELDS.forEach((field) => {
        if (saved[field] !== available[field]) changed.add(field);
    });
    const hasChange = (fields) => fields.some((field) => changed.has(field));
    const releaseChanged = hasChange(RELEASE_FIELDS);
    const releaseIdChanged = changed.has('factor_release_id');
    const releaseValue = (context, isAvailable) => {
        const release = display(context.factor_release_id);
        if (releaseChanged && !releaseIdChanged) {
            return `${release} (${isAvailable ? 'updated' : 'previous'} release binding)`;
        }
        return release;
    };
    const rows = [
        { label: 'Factor provider', chip: 'Factor provider', fields: ['vendor'], savedValue: display(saved.vendor), availableValue: display(available.vendor) },
        { label: 'Factor model', chip: 'Factor model', fields: ['factor_model', 'factor_horizon'], savedValue: joined(saved.factor_model, saved.factor_horizon), availableValue: joined(available.factor_model, available.factor_horizon) },
        { label: 'Factor data date', chip: 'Factor data date', fields: ['factor_as_of_date'], savedValue: display(saved.factor_as_of_date), availableValue: display(available.factor_as_of_date) },
        { label: 'Factor release', chip: 'Factor release', fields: RELEASE_FIELDS, savedValue: releaseValue(saved, false), availableValue: releaseValue(available, true) },
        { label: 'Security Resolution snapshot', chip: 'Security Resolution', fields: ['msi_snapshot_version'], savedValue: display(saved.msi_snapshot_version), availableValue: display(available.msi_snapshot_version) },
    ];
    if (hasChange(SETTING_FIELDS) && !hasChange(PRIMARY_FACTOR_FIELDS)) {
        rows.push({
            label: 'Factor settings', chip: 'Factor settings', fields: SETTING_FIELDS,
            savedValue: 'Previous settings', availableValue: 'Updated settings',
        });
    }
    if (hasChange(SOURCE_RULE_FIELDS) && !changed.has('vendor')) {
        rows.push({
            label: 'Coverage rules', chip: 'Coverage rules', fields: SOURCE_RULE_FIELDS,
            savedValue: 'Previous rules', availableValue: 'Updated rules',
        });
    }
    return rows.map((row) => ({
        ...row,
        differs: hasChange(row.fields) || row.savedValue !== row.availableValue,
    }));
}

export function watchlistContextChangeLabels({ saved, available, changedFields = [] } = {}) {
    if (!saved || !available) return [];
    return contextRows(saved, available, changedFields)
        .filter((row) => row.differs)
        .map((row) => row.chip);
}

export default function WatchlistContextComparison({
    saved,
    available,
    changedFields = [],
    savedLabel = 'Published Watchlist',
    availableLabel = 'Available now',
}) {
    if (!saved || !available) return null;
    const rows = contextRows(saved, available, changedFields);

    return (
        <TableContainer sx={watchlistTableFrameSx}>
            <Table size="small" aria-label="Watchlist context comparison">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: '27%' }}>Setting</TableCell>
                        <TableCell sx={{ width: '36%' }}>{savedLabel}</TableCell>
                        <TableCell>{availableLabel}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => {
                        const highlight = row.differs ? {
                            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.09),
                        } : undefined;
                        return (
                            <TableRow key={row.label} data-context-changed={row.differs ? 'true' : 'false'}>
                                <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                                <TableCell sx={highlight}>
                                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                        {row.savedValue}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={highlight}>
                                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                        {row.availableValue}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

WatchlistContextComparison.propTypes = {
    saved: PropTypes.object,
    available: PropTypes.object,
    changedFields: PropTypes.arrayOf(PropTypes.string),
    savedLabel: PropTypes.string,
    availableLabel: PropTypes.string,
};
